// W4-7 — THE LIVE VIEW. Owner ruling, 2026-07-29.
//
// HER RULING: "interim transcription shipped but the input doesn't scroll/grow with long dictation,
// past a point she couldn't see her words. The input area must keep the latest words visible while
// speaking (auto-scroll or grow within limits) and allow full review of the whole message before send.
// Site-wide through the shared mic component."
//
// WHAT W1-13 ACTUALLY SHIPPED, AND WHY IT WAS ONLY HALF THE FEATURE. Interim results turned the words
// on, and the composition law made a growing guess replace the last one instead of appending. Both
// correct. But the Ask Lala composer, on BOTH surfaces, was a single-line `<input>`, and a browser does
// not scroll the caret into view when the value is set PROGRAMMATICALLY rather than typed. So the field
// held her whole sentence and showed her the first few words of it, forever, while she kept talking.
// Nothing was lost; it was invisible, which for dictation is the same thing.
//
// TWO SEPARATE PROMISES, and it matters that they are separate, because one of them a single-line
// field cannot keep at all:
//   1. WHILE SPEAKING: the latest words stay visible. That is a scroll (or a grow, then a scroll).
//   2. BEFORE SENDING: the whole message can be reviewed. That needs a field that can SHOW more than
//      one line, which is why the composer becomes a textarea that grows within limits. A one-line
//      box that scrolls perfectly still cannot let her read back a paragraph.
//
// This module is the geometry, pure, and it imports NOTHING so `scripts/w4-live-view.mjs` can RUN it
// against fake metrics. The DOM half (`applyLiveView`) is four assignments and is here too, so there is
// one home for the whole behaviour rather than a copy at every mic.

// What the law needs to know about a field. Exactly the four numbers a real element reports, so a gate
// can hand it a plain object and a browser can hand it an element.
export type FieldMetrics = {
  scrollHeight: number;   // how tall the content wants to be
  clientHeight: number;   // how tall the box actually is
  scrollTop: number;      // where it is scrolled now
  scrollWidth?: number;   // single-line fields scroll sideways instead
  clientWidth?: number;
};

// Grow within limits. `min` is where an empty composer sits (one line, so the chat does not open with a
// giant box) and `max` is where growing stops and scrolling takes over (so a long dictation can never
// push Send off the screen, which would be W1-12's below-the-fold defect in a new place).
export type GrowLimits = { min: number; max: number };

export const LALA_COMPOSE_LIMITS: GrowLimits = { min: 44, max: 168 };
// A phone gets a lower ceiling: 168px of composer on a 390px-tall keyboard-shrunk viewport is most of
// what is left of the thread.
export const LALA_COMPOSE_LIMITS_PHONE: GrowLimits = { min: 44, max: 112 };

// The phone break is the site's existing one (940px, the same watcher the tier art uses), so the
// composer's ceiling changes at the same width everything else does rather than at a number of its own.
export function limitsForWidth(width: number): GrowLimits {
  return Number(width) > 0 && Number(width) <= 940 ? LALA_COMPOSE_LIMITS_PHONE : LALA_COMPOSE_LIMITS;
}

// **THE MESSAGE COMPOSER'S OWN CEILING (her order, 2026-08-07): "roughly a third of the screen".**
//
// It is a THIRD OF THE VIEWPORT rather than a pixel count, and that is her ruling read literally — a
// fixed ceiling is the class ITEM 2 spent this morning removing from the drawer button, where a
// number measured against one screen was still being applied on every other one.
//
// **It lives HERE, beside Lala's two, because the MECHANISM must stay single.** The first draft of
// her composer round wrote a second clamp-and-overflow pair in `src/livingRoom.js`; two grow laws
// drift, and a box that grows correctly in one composer and not in another is the report she just
// made. What differs between composers is the LIMITS, so the limits are what each one declares.
export const MESSAGE_COMPOSE_MAX_FRACTION = 1 / 3;
export function messageComposeLimits(viewportHeight: number): GrowLimits {
  const h = Number(viewportHeight) || 0;
  // With no viewport to measure — a server render, a detached node — it takes Lala's desktop ceiling
  // rather than growing without bound. "Nothing to measure" is not "an infinite screen" (R18).
  if (h <= 0) return LALA_COMPOSE_LIMITS;
  return { min: 44, max: Math.max(44, Math.round(h * MESSAGE_COMPOSE_MAX_FRACTION)) };
}

export function clampGrow(contentHeight: number, limits: GrowLimits): number {
  const c = Number(contentHeight);
  if (!Number.isFinite(c)) return limits.min;
  return Math.max(limits.min, Math.min(limits.max, Math.ceil(c)));
}

// Is the content taller than the box can grow to? Then the box has hit its ceiling and the rest of the
// promise is kept by scrolling.
export function overflows(contentHeight: number, limits: GrowLimits): boolean {
  return Math.ceil(Number(contentHeight) || 0) > limits.max;
}

// WHERE THE LATEST WORDS ARE. The bottom of the content, which is where dictation appends. Never
// negative, and 0 when nothing overflows, so a short message is never scrolled away from its start.
export function latestScrollTop(m: FieldMetrics): number {
  const h = Number(m?.scrollHeight) || 0;
  const c = Number(m?.clientHeight) || 0;
  return Math.max(0, h - c);
}

// A single-line field cannot grow, so the latest words live at the right-hand end.
export function latestScrollLeft(m: FieldMetrics): number {
  const w = Number(m?.scrollWidth) || 0;
  const c = Number(m?.clientWidth) || 0;
  return Math.max(0, w - c);
}

// Already showing the latest words? Then nothing is done, which keeps a member who has deliberately
// scrolled UP to re-read the top of their own message from being yanked back down mid-read. The
// tolerance is a couple of pixels because sub-pixel line heights are normal.
export function alreadyAtLatest(m: FieldMetrics, tolerance = 2): boolean {
  return Math.abs(latestScrollTop(m) - (Number(m?.scrollTop) || 0)) <= tolerance;
}

type LiveField = {
  tagName?: string;
  value?: string;
  style?: { height?: string; overflowY?: string };
  scrollHeight: number;
  clientHeight: number;
  scrollTop: number;
  scrollWidth?: number;
  clientWidth?: number;
  scrollLeft?: number;
  setSelectionRange?: (start: number, end: number) => void;
};

// THE ONE APPLICATION. Grow the field to fit its content up to the ceiling, then keep the newest words
// in view. Safe on null (every call site passes a ref that is null for one render) and safe on an
// `<input>`, which is why a field that has not been converted to a textarea yet still behaves.
// `limits` is OPTIONAL, and its absence is a real instruction rather than a default: her ruling offers
// "auto-scroll OR grow within limits", and the fields that already have a designed height (the journal
// draft, the two craft-panel textareas at rows=4) want the SCROLL and must not be resized out from
// under their own layout. Pass limits to grow; omit them to scroll only.
export function applyLiveView(el: LiveField | null | undefined, limits?: GrowLimits | null): boolean {
  if (!el) return false;
  const single = String(el.tagName || "").toUpperCase() === "INPUT";
  if (single) {
    // No growing here, so the whole promise is the sideways scroll. The caret goes to the end too:
    // some browsers only honour scrollLeft on a focused field once the selection is there.
    const left = latestScrollLeft(el);
    el.scrollLeft = left;
    return true;
  }
  if (limits && el.style) {
    // Measure the CONTENT, not the box: a box already grown to 168px reports scrollHeight 168 and
    // would never shrink back when she deletes a paragraph. Collapsing to auto first is what makes
    // this reversible in both directions.
    el.style.height = "auto";
    const content = el.scrollHeight;
    el.style.height = `${clampGrow(content, limits)}px`;
    el.style.overflowY = overflows(content, limits) ? "auto" : "hidden";
  }
  el.scrollTop = latestScrollTop(el);
  return true;
}

// >>> **THE ANCHOR CONVERGES RATHER THAN GUESSING TWICE.** <<< (#133's probe, 2026-08-21.)
//
// W7-2 gave the scroll-to-newest TWO passes for a measured reason: a picture in the row that just
// arrived has NO HEIGHT until it loads, so a single pass lands short of the message it was aimed at.
// That was exactly right for a row or two of media, and PERF-1 then put `content-visibility: auto`
// with a `contain-intrinsic-size` of 166px on every message in the room — so in a long thread the
// scroll height at anchor time is an ESTIMATE of hundreds of rows nobody has laid out yet, and it
// keeps moving for several frames as the browser renders the ones near the viewport.
//
// **MEASURED IN A REAL CHROME AT ELITE 2026's OWN DIMENSIONS — 872 rows, 393x852.** Where the real
// rows are SHORTER than the placeholder the two passes hold (the browser's own scroll anchoring
// keeps the foot pinned). Where they are TALLER — a message carrying a picture, or a long one
// wrapping on a phone — the height GROWS underneath the anchor and the two passes land
// **1,386px SHORT of the newest message, with the newest row off screen**: W7-2's own defect
// reappearing inside the room PERF-1 containerised, and it scales with the length of the thread.
//
// So the law is not a third hard-coded pass — the error is unbounded, so any fixed number is wrong
// at some length. It re-anchors while the height is still MOVING and stops when it settles.
export const ANCHOR_MAX_PASSES = 8;

/**
 * Should the scroll be re-anchored to the foot?
 *
 * `pass < 2` is UNCONDITIONAL, which keeps W7-2's two measured passes exactly as they shipped — this
 * law can only ever add passes to a thread whose height is still moving, so a short thread behaves
 * byte-identically to what it does today (ITEM 12's neutrality argument).
 *
 * It is BOUNDED, because a row whose media never finishes loading would otherwise re-anchor every
 * frame forever — a spin on the surface PERF-1 spent a whole round making cheap.
 *
 * And it stops the moment she is no longer at the foot: a converging loop runs across frames, so
 * without this clause a member who scrolls up one frame after opening a long thread is yanked back
 * down by a pass that was already in flight (S6-1's own exception, one surface over).
 */
export function anchorAgain(
  { pass = 0, height = 0, lastHeight = -1, stuck = true }:
  { pass?: number; height?: number; lastHeight?: number; stuck?: boolean } = {},
): boolean {
  if (stuck !== true) return false;
  if (typeof height !== "number" || !Number.isFinite(height) || height <= 0) return false;
  if (typeof pass !== "number" || !Number.isFinite(pass) || pass < 0) return false;
  if (pass >= ANCHOR_MAX_PASSES) return false;
  if (pass < 2) return true;
  return height !== lastHeight;
}

/**
 * >>> **ITEM 2 — HOW FAR THE NEWEST ROW HAS TO CLEAR THE STICKY DOCK, MEASURED RATHER THAN TYPED.**
 * <<<
 *
 * The Ask Lala thread anchors its newest row with `scrollIntoView({ block: "end" })`, and the
 * compose bar is `position: sticky`, so the browser can judge a row "in view" while a sticky element
 * is sitting on top of it — `scrollIntoView` knows nothing about what covers what. `scroll-margin-
 * bottom` is the one lever that moves it, and it was a hand-typed **84px** in the stylesheet.
 *
 * **THAT NUMBER ROTTED WHERE NOBODY COULD SEE IT, WHICH IS THE ONE REPAIR THIS HOUSE HAS ALREADY
 * PROVED WRONG ON THIS PAIR OF SURFACES** (ITEM2-DOCK-1: *"S6-2 chose 176px against a composer that
 * THE COMPOSER GROWS then made grow"*). The dock has grown TWICE since 84 was chosen — N2-3 gave the
 * phone its own attach/mic/Send row, and FUNNEL-4 mounts the free-turn counter INSIDE the compose
 * (`.ask-lala-compose .lala-turns { order: -1 }`), so the funnel cohort's dock is taller than any
 * seat the FAST gate measures. W7-2's own check runs on a member with NO allotment, so no counter is
 * drawn and the bar it measures is the short one: **the one screen where this is wrong is the one
 * screen nothing was looking at.**
 *
 * Measured at coordinates on a staged funnel seat (`scripts/talk-arrival-walk.mjs`, 393x852): the
 * dock's top edge sat **241px** above the viewport foot and the margin offered **84**, so the row's
 * scroll-target box was already inside the viewport, the page did not move at all (`scrollY=0` of
 * 317 available) and **her greeting ran 52px under the counter — the first thing a stranger reads.**
 * The hit test at the greeting's own centre named the thing covering it: `p.lala-turns`. Desktop
 * needed 86 and was passing on 84 by luck of layout.
 *
 * **IT IS THE DISTANCE TO THE DOCK'S TOP AND NOT THE DOCK'S HEIGHT**, because whatever sits BELOW
 * the dock covers her too — on a phone that is the mobile tab bar, which is 84px of the 241.
 *
 * A dock that is not mounted clears NOTHING rather than a guessed number, so every surface with no
 * compose bar on it is byte-identical (ITEM 12's neutrality argument) and the failure mode of this
 * whole mechanism is exactly `scrollIntoView`'s own behaviour rather than something worse (RAIL
 * RESTORE's ruling on `var(--nav-real-h, var(--nav-h))`).
 *
 * **AND IT NEVER ANSWERS A NEGATIVE**, which is `--gen-follow-lift`'s own measured case one surface
 * over: a compose scrolled off the foot of the viewport reports a top edge BELOW it, and a negative
 * scroll margin would push the newest row further under the thing it is supposed to clear.
 *
 * The window and the element are PARAMETERS, so a `.mjs` gate RUNS every one of those states with no
 * browser — which is the whole reason this law is here and not inline in a twelve-thousand-line
 * component (R8/R95).
 */
export function dockClearance(
  win: { innerHeight?: unknown } | null | undefined,
  dock: { getBoundingClientRect?: () => { top?: unknown } } | null | undefined,
): number {
  if (!dock || typeof dock.getBoundingClientRect !== "function") return 0;
  const height = win ? win.innerHeight : null;
  if (typeof height !== "number" || !Number.isFinite(height)) return 0;
  let top: unknown = null;
  try { top = dock.getBoundingClientRect()?.top; } catch { return 0; }
  if (typeof top !== "number" || !Number.isFinite(top)) return 0;
  return Math.max(0, Math.round(height - top));
}
